import { httpsCallable } from "firebase/functions";
import { firebaseConfig } from "../firebase/configKeys";
import {
  functions,
  FUNCTIONS_EMULATOR_PORT,
} from "../firebase/firebase.utils";

const FUNCTIONS_REGION = "australia-southeast1";

const MINT_HTTP_NAME = "onlineOrdering-mintOnlineOrderingSession";
const GET_SURCHARGE_CALLABLE_NAME = "onlineOrdering-getSurchargeConfigs";
const GET_DISCOUNT_BY_CODE_CALLABLE_NAME = "onlineOrdering-getDiscountByCode";

/**
 * HTTPS URL for `mintOnlineOrderingSession` (POST JSON `{ kitchenId }`).
 */
export function getMintOnlineOrderingSessionUrl(): string {
  const { projectId } = firebaseConfig;
  const isDev = process.env.REACT_APP_ENV === "development";
  if (isDev) {
    return `http://127.0.0.1:${FUNCTIONS_EMULATOR_PORT}/${projectId}/${FUNCTIONS_REGION}/${MINT_HTTP_NAME}`;
  }
  return `https://${FUNCTIONS_REGION}-${projectId}.cloudfunctions.net/${MINT_HTTP_NAME}`;
}

export type MintSessionResponse = {
  sessionToken: string;
  expiresInSeconds: number;
};

/**
 * Public mint endpoint (no secrets). Browser sends `Origin`; Cloud Function
 * checks `ONLINE_ORDERING_ALLOWED_ORIGINS`.
 */
export async function fetchOnlineOrderingSessionToken(
  kitchenId: string,
): Promise<MintSessionResponse> {
  const url = getMintOnlineOrderingSessionUrl();
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ kitchenId }),
  });
  const data = (await res.json()) as MintSessionResponse & { error?: string };
  if (!res.ok) {
    throw new Error(data?.error || `Mint failed (${res.status})`);
  }
  if (!data.sessionToken) {
    throw new Error("Mint response missing sessionToken");
  }
  return data;
}

type SessionCacheEntry = {
  sessionToken: string;
  /** Client-side deadline to mint again (buffer before JWT exp). */
  validUntilMs: number;
};

const sessionCacheByKitchen = new Map<string, SessionCacheEntry>();

/** Renew before actual JWT expiry (skew + network margin). */
const SESSION_RENEW_BUFFER_SEC = 60;

/**
 * Returns a cached session for `kitchenId` if still valid; otherwise mints and caches.
 */
export async function getOrFetchOnlineOrderingSessionToken(
  kitchenId: string,
): Promise<MintSessionResponse> {
  const now = Date.now();
  const cached = sessionCacheByKitchen.get(kitchenId);
  if (cached && now < cached.validUntilMs) {
    return {
      sessionToken: cached.sessionToken,
      expiresInSeconds: Math.max(
        1,
        Math.ceil((cached.validUntilMs - now) / 1000),
      ),
    };
  }

  const fresh = await fetchOnlineOrderingSessionToken(kitchenId);
  const ttlSec = fresh.expiresInSeconds;
  const validUntilMs =
    now + Math.max(0, ttlSec - SESSION_RENEW_BUFFER_SEC) * 1000;
  sessionCacheByKitchen.set(kitchenId, {
    sessionToken: fresh.sessionToken,
    validUntilMs,
  });
  return fresh;
}

export type SurchargeConfigsResult = { surchargeConfigs: unknown };

/**
 * Callable `onlineOrdering-getSurchargeConfigs` (requires session from mint).
 */
export async function fetchSurchargeConfigsCallable(
  kitchenId: string,
  sessionToken: string,
): Promise<SurchargeConfigsResult> {
  const fn = httpsCallable<
    { kitchenId: string; sessionToken: string },
    SurchargeConfigsResult
  >(functions, GET_SURCHARGE_CALLABLE_NAME);
  const { data } = await fn({ kitchenId, sessionToken });
  return data;
}

/** Mint + load surcharge configs in one helper. */
export async function loadSurchargeConfigsForKitchen(
  kitchenId: string,
): Promise<SurchargeConfigsResult> {
  const { sessionToken } = await getOrFetchOnlineOrderingSessionToken(kitchenId);
  return fetchSurchargeConfigsCallable(kitchenId, sessionToken);
}

export type GetDiscountByCodeResult = {
  discount: Record<string, unknown> | null;
};

/** Some logs / proxies show `{ result: { discount } }` instead of `{ discount }`. */
function normalizeGetDiscountByCodeResult(
  data: unknown,
): GetDiscountByCodeResult {
  if (!data || typeof data !== "object") {
    return { discount: null };
  }
  const o = data as Record<string, unknown>;
  if (
    "result" in o &&
    !("discount" in o) &&
    o.result &&
    typeof o.result === "object" &&
    "discount" in (o.result as object)
  ) {
    return o.result as GetDiscountByCodeResult;
  }
  return o as GetDiscountByCodeResult;
}

/**
 * Callable `onlineOrdering-getDiscountByCode` (requires session from mint).
 */
export async function fetchDiscountByCodeCallable(
  kitchenId: string,
  sessionToken: string,
  discountCode: string,
): Promise<GetDiscountByCodeResult> {
  const fn = httpsCallable<
    { kitchenId: string; sessionToken: string; discountCode: string },
    GetDiscountByCodeResult
  >(functions, GET_DISCOUNT_BY_CODE_CALLABLE_NAME);
  const { data } = await fn({ kitchenId, sessionToken, discountCode });
  return normalizeGetDiscountByCodeResult(data);
}

/** Mint + resolve discount by code in one helper. */
export async function loadDiscountByCodeForKitchen(
  kitchenId: string,
  discountCode: string,
): Promise<GetDiscountByCodeResult> {
  const { sessionToken } = await getOrFetchOnlineOrderingSessionToken(kitchenId);
  return fetchDiscountByCodeCallable(kitchenId, sessionToken, discountCode);
}
